import React from 'react';
import Box from '@mui/material/Box';
import Switch from '@mui/material/Switch';
import Typography from '@mui/material/Typography';
import { styled } from '@mui/material/styles';

export interface ToggleInputProps {
  toggled: boolean;
  handleToggle: () => void;
  name?: string;
  informationText?: string;
  disabled?: boolean;
  className?: string;
}

const StyledMuiSwitch = styled(Switch)(({ theme }) => ({
  width: 36,
  height: 20,
  padding: 0,
  display: 'flex',
  '&:active': {
    '& .MuiSwitch-thumb': {
      width: 17,
    },
    '& .MuiSwitch-switchBase.Mui-checked': {
      transform: 'translateX(15px)',
    },
  },
  '& .MuiSwitch-switchBase': {
    padding: 2,
    transitionDuration: '250ms',
    '&.Mui-checked': {
      transform: 'translateX(16px)',
      color: '#fff',
      '& + .MuiSwitch-track': {
        background: 'linear-gradient(135deg, #059669 0%, #10b981 100%)',
        opacity: 1,
        border: 0,
      },
      '&.Mui-disabled + .MuiSwitch-track': {
        opacity: 0.5,
      },
    },
    '&.Mui-focusVisible .MuiSwitch-thumb': {
      color: '#10b981',
      border: '6px solid #fff',
    },
    '&.Mui-disabled .MuiSwitch-thumb': {
      color: theme.palette.grey[100],
    },
    '&.Mui-disabled + .MuiSwitch-track': {
      opacity: 0.7,
    },
  },
  '& .MuiSwitch-thumb': {
    boxShadow: '0 2px 4px 0 rgba(0, 35, 11, 0.2)',
    width: 16,
    height: 16,
    borderRadius: 8,
    transition: theme.transitions.create(['width'], {
      duration: 200,
    }),
  },
  '& .MuiSwitch-track': {
    borderRadius: 20 / 2,
    opacity: 1,
    backgroundColor: '#e2e8f0',
    boxSizing: 'border-box',
    transition: theme.transitions.create(['background-color'], {
      duration: 250,
    }),
  },
}));

export const ToggleInput = ({
  toggled,
  handleToggle,
  name = 'toggle',
  informationText,
  disabled = false,
  className,
}: ToggleInputProps) => {
  return (
    <Box
      className={className}
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 1.25,
      }}
    >
      <StyledMuiSwitch
        checked={Boolean(toggled)}
        onChange={disabled ? undefined : handleToggle}
        disabled={disabled}
        inputProps={{
          'aria-label': name,
          role: 'switch',
        }}
      />
      {informationText && (
        <Typography
          variant="body2"
          sx={{
            fontSize: '0.75rem',
            fontWeight: 500,
            color: disabled ? 'text.disabled' : 'text.secondary',
            lineHeight: 1.3,
            userSelect: 'none',
          }}
        >
          {informationText}
        </Typography>
      )}
    </Box>
  );
};

export default ToggleInput;
